
import React from 'react';
import { formatDate } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";
import { enUS, fr } from 'date-fns/locale';
import { cn } from "@/lib/utils";
import { WeeklyTimeSlotColumn } from "./WeeklyTimeSlotColumn";
import { WeeklyDayColumn } from "./WeeklyDayColumn";
import { generateTimeSlots } from "@/utils/timeSlots";
import { useIsMobile } from "@/hooks/use-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTheme } from "@/components/theme/ThemeProvider";
import { useScheduleSettings } from "@/hooks/useSlotDuration";
import { processTimeSlotsWithGaps } from "@/utils/scheduleUtils";
import { useThemeCustomization } from '@/hooks/useThemeCustomization';

interface WeeklyScheduleGridProps {
  weekDates: Date[];
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onAddClass: (date: Date, time: string) => void;
  onEditClass: (classData: any, date: Date) => void;
  isAdmin: boolean;
  isLoading: boolean;
  getClassForTimeSlot: (date: Date, timeSlot: string) => any;
  getClassesForTimeSlot?: (date: Date, timeSlot: string) => any[];
  onHandleClassClick: (classInfo: any, date: Date) => void;
  isClassBooked?: (classId: string, date: Date, time: string) => boolean;
  recentlyBookedSlots?: Set<string>;
  realtimeModifiedSlots?: Set<string>;
  bookingAdvanceDays?: number;
}

export const WeeklyScheduleGrid: React.FC<WeeklyScheduleGridProps> = ({
  weekDates,
  selectedDate,
  onDateSelect,
  onAddClass,
  isAdmin,
  isLoading,
  getClassForTimeSlot,
  getClassesForTimeSlot,
  onHandleClassClick,
  isClassBooked = () => false,
  recentlyBookedSlots = new Set(),
  realtimeModifiedSlots = new Set(),
  bookingAdvanceDays = 0
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'fr' ? fr : enUS;
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { slotDuration, startTime, endTime } = useScheduleSettings();
  const { scheduleDensity } = useThemeCustomization();
  // VB20 uses the same compact grid for athletes and staff. Role only controls
  // available actions; it must not change the brand presentation.
  const compactVb20Grid = scheduleDensity === 'compact';
  const allTimeSlots = generateTimeSlots(slotDuration, startTime, endTime);

  // Traiter les créneaux pour regrouper les vides consécutifs en gaps
  // Passe slotDuration pour calculer correctement les créneaux couverts par la durée des cours
  const processedSlots = processTimeSlotsWithGaps(
    allTimeSlots,
    weekDates,
    getClassForTimeSlot,
    isAdmin,
    slotDuration
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className={cn("text-muted-foreground", isMobile && "text-xs")}>Loading...</p>
      </div>
    );
  }

  // Check for empty weekDates array
  if (!weekDates || weekDates.length === 0) {
    console.error("WeeklyScheduleGrid: weekDates is empty or undefined");
    return (
      <div className="flex items-center justify-center py-16">
        <p className={cn("text-muted-foreground", isMobile && "text-xs")}>No dates available</p>
      </div>
    );
  }

  const hasClasses = weekDates.some(date => {
    return allTimeSlots.some(time => {
      if (getClassesForTimeSlot) return getClassesForTimeSlot(date, time).length > 0;
      return getClassForTimeSlot(date, time) !== undefined;
    });
  });

  if (!hasClasses && !isLoading && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <p className={cn("text-muted-foreground text-center", isMobile && "text-xs")}>
          Aucun cours disponible pour cette semaine
        </p>
        <p className={cn("text-muted-foreground text-center text-sm", isMobile && "text-xs")}>
          Essayez de naviguer vers une autre semaine ou contactez votre administrateur
        </p>
      </div>
    );
  }

  const numDays = weekDates.length;

  return (
    <div className={cn(
      "athlete-week-grid border rounded-md shadow-sm overflow-hidden",
      compactVb20Grid && "vb20-week-grid",
      isDarkMode ? "border-muted bg-card" : "bg-white"
    )}>
      <div className="relative">
        {/* Time slots column - sticky left */}
        <div className={cn(
          "absolute left-0 top-0 z-20 border-r",
          isDarkMode ? "bg-card border-muted" : "bg-white border-gray-200"
        )}>
          <WeeklyTimeSlotColumn processedSlots={processedSlots} slotDuration={slotDuration} compact={compactVb20Grid} />
        </div>
        
        {/* Scrollable content */}
        <ScrollArea className={cn("w-full overflow-x-auto scroll-smooth", isMobile && "max-w-[calc(100vw-16px)]")}>
          <div 
            className={cn(
              "grid gap-[1px]",
              isMobile ? "" : "min-w-[900px]",
              isMobile ? "pl-[26px]" : "pl-[55px]"
            )}
            style={{
              gridTemplateColumns: isMobile
                ? `auto repeat(${numDays}, minmax(0, 1fr))`
                : `auto repeat(${numDays}, minmax(60px, 1fr))`
            }}
          >
            {/* Days columns (hidden first column to account for sticky column) */}
            <div className="opacity-0 w-0 overflow-hidden"></div>
            
            {weekDates.map((date, index) => (
              <WeeklyDayColumn
                key={formatDate(date, 'yyyy-MM-dd')}
                date={date}
                index={index}
                processedSlots={processedSlots}
                slotDuration={slotDuration}
                startTime={startTime}
                selectedDate={selectedDate}
                getClassForTimeSlot={getClassForTimeSlot}
                getClassesForTimeSlot={getClassesForTimeSlot}
                isAdmin={isAdmin}
                onDateSelect={onDateSelect}
                onAddClass={onAddClass}
                onHandleClassClick={onHandleClassClick}
                isClassBooked={isClassBooked}
                locale={locale}
                isMobile={isMobile}
                recentlyBookedSlots={recentlyBookedSlots}
                realtimeModifiedSlots={realtimeModifiedSlots}
                bookingAdvanceDays={bookingAdvanceDays}
                compact={compactVb20Grid}
              />
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
